import Calendar from '@/components/calendar/Calendar';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import FileUpload from '@/components/general/FileUpload';
import ImageUpload from '@/components/general/ImageUpload';
import ImageUploadShow from '@/components/general/ImageUploadShow';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import { DATE_YMD_2, DEFAULT_BOOKING_LEAD_TIME, DISPPLAY_MOMENT_YMD_HM, ekDomain, MAX_LENGTH_LINK, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { FileInfor, FileResponse } from '@/types/bike';
import { APPROVE_BIKE_STATUS, COMMON_TYPE, FORM_TYPE, FormType, GEARBOX, LANGUAGES_SUPPORTED, ROLE_OWNER, ROLE_USER, STATUS } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { cn, isRoles } from '@/utils/utils';
import { validateUrl } from '@/utils/validate';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { Trash2 } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

interface BasicInformationProps {
    type: FormType,
    onComplete?: () => void
}

export default observer(function BasicInformation({ type, onComplete }: BasicInformationProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { id } = useParams();

    // const
    const isEk = isPathEk(pathname);
    const isView = type === FORM_TYPE.View;
    const isEdit = type === FORM_TYPE.Edit;
    const isCreate = type === FORM_TYPE.Create;
    const isOwner = isRoles([...ROLE_OWNER]);

    const accessoryFieldsRadio: { name: keyof FormData, label: string }[] = [
        { name: 'isSmartphoneHolder', label: t('field.smartphone_holder') },
        { name: 'isUsbCharging', label: t('field.usb_charging') },
        { name: 'isEtc', label: t('field.etc') },
        { name: 'isTopBox', label: t('field.top_box') }
    ];

    // validate schema
    const validateSchema = yup.object().shape({
        bikeOwnerId: yup
            .string()
            .required('form_error_validate.required'),
        bikeLicense: yup
            .string()
            .required('form_error_validate.required'),
        bikeManualUrl: yup
            .string().test(
                'validate-url',
                t('server_messages.URL_NOT_FORMAT'),
                (value) => {
                    return validateUrl(value);
                }
            ).optional(),
        bikeIdentification: isEk ?
            yup
                .string() :
            yup
                .string()
                .required('form_error_validate.required'),
        bookingLeadTime: yup.string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .required('form_error_validate.required')
            .test(
                'min-number',
                t('form_error_validate.min', { min: DEFAULT_BOOKING_LEAD_TIME }),
                val => Number(val) >= DEFAULT_BOOKING_LEAD_TIME
            ),
        bikeModel: yup
            .string()
            .required('form_error_validate.required'),
        bikeCapacityId: isEk ?
            yup
                .string() :
            yup
                .string()
                .required('form_error_validate.required'),
        bikeManufacture: yup
            .string()
            .required('form_error_validate.required'),
        waitingTimeUntilNextBooking: yup.string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number')).test(
                'min-value',
                t('form_error_validate.min', { min: 0 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num >= 0;
                }
            ),
        bikeType: yup
            .string()
            .required('form_error_validate.required'),
        bikeTransmission: !isEk ?
            yup
                .string()
                .required('form_error_validate.required') :
            yup.string(),
        isShowWeb: yup
            .boolean()
            .required('form_error_validate.required'),
        vehicleImageBeforeId: yup
            .string(),
        vehicleImageAfterId: yup
            .string(),
        vehicleImageLeftId: yup
            .string(),
        vehicleImageRightId: yup
            .string()
            .required('form_error_validate.required'),
        certificateExpiryDate: yup
            .date(),
        certificateImageIds: isEk ?
            yup
                .array()
                .of(yup.string())
                .min(1, 'form_error_validate.required') :
            yup.array()
                .of(yup.string()),
        isSmartphoneHolder: yup
            .boolean(),
        isEtc: yup
            .boolean(),
        isUsbCharging: yup
            .boolean(),
        // isFallOverSafetySystem: yup
        //     .boolean(),
        isTopBox: yup
            .boolean(),
        note: yup
            .string(),
        // color: isEk ?
        //     yup
        //         .string()
        //         .required('form_error_validate.required') :
        //     yup
        //         .string(),
        batteryReplacementTime: isEk ?
            yup.date().required('form_error_validate.required') :
            yup.date().nullable().notRequired(),
        // rentalStartDate: isEk ?
        //     yup
        //         .date()
        //         .required('form_error_validate.required') :
        //     yup
        //         .date(),
        pinCode: isEk ? yup.string().checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number')) : yup.string().checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number')).required('form_error_validate.required'),
        compensation: yup.string().trim().checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number')).test(
            'min-value',
            t('form_error_validate.min', { min: 0 }),
            (val) => {
                if (val == null || val === '') {
                    return true;
                }
                const num = Number(val);
                return num >= 0;
            }
        ),
        isRecommendation: yup.boolean(),
        compulsoryInsuranceExpDate: yup.date().required('form_error_validate.required'),
        commisisonRate: yup
            .string()
            .trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .test(
                'min-value',
                t('form_error_validate.min', { min: 0 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num >= 0;
                }
            )
            .test(
                'max-value',
                t('form_error_validate.max', { max: 100 }),
                (val) => {
                    if (val == null || val === '') {
                        return true;
                    }
                    const num = Number(val);
                    return num <= 100;
                }
            )
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // store
    const {
        accountStore: { accounts, getAccounts, paging: accountPaging },
        capacitieStore: { capacitieList, getCapacitieList },
        bikeStore: { bikeDetail, createBikeEk, updateBikeEk, createBikeGas, updateBikeGas, getBikeDetail, chekBikeHasOrderBeforeOff, deleteBike },
        generalStore: { manufacturers, bikeTypes, bikeTypesEK, getManufacturers, getBikeTypes, getBikeTypesEK },
        authStore: { profileEk, profileGas },
        modalStore: { showAlertModal, hideModal }
    } = useStore();

    // get id
    const newId = id ?? bikeDetail?.id;

    // state
    const [isOnlyView, setOnlyView] = useState<boolean>(false);

    const [urlImagePreviewBefore, setUrlImagePreviewBefore] = useState<string>('');
    const [urlImagePreviewAfter, setUrlImagePreviewAfter] = useState<string>('');
    const [urlImagePreviewLeft, setUrlImagePreviewLeft] = useState<string>('');
    const [urlImagePreviewRight, setUrlImagePreviewRight] = useState<string>('');
    const [fileCerUploads, setFileCerUploads] = useState<FileInfor[] | null>([]);

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            bikeOwnerId: isOwner ? (isEk ? profileEk?.id.toString() : profileGas?.id.toString()) : '',
            bikeLicense: '',
            bikeManualUrl: '',
            bikeIdentification: '',
            bookingLeadTime: DEFAULT_BOOKING_LEAD_TIME.toString(),
            bikeModel: '',
            bikeCapacityId: '',
            bikeManufacture: '',
            waitingTimeUntilNextBooking: '',
            bikeType: '',
            bikeTransmission: GEARBOX.MT,
            isShowWeb: true,
            vehicleImageBeforeId: '',
            vehicleImageAfterId: '',
            vehicleImageLeftId: '',
            vehicleImageRightId: '',
            certificateExpiryDate: undefined,
            certificateImageIds: [],
            isSmartphoneHolder: false,
            isEtc: false,
            isUsbCharging: false,
            // isFallOverSafetySystem: false,
            isTopBox: false,
            note: '',
            // color: '',
            batteryReplacementTime: undefined,
            // rentalStartDate: undefined,
            pinCode: '',
            compensation: '',
            isRecommendation: false,
            compulsoryInsuranceExpDate: undefined,
            commisisonRate: undefined
        }
    });

    const watchVehicleImageRight = form.watch('vehicleImageRightId');
    const watchCertificateImageIds = form.watch('certificateImageIds');

    // life cycle
    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        form.reset({
            bikeOwnerId: (bikeDetail?.bikeOwnerId ?? (isCreate ? (isEk ? profileEk?.id ?? '' : profileGas?.id ?? '') : '')).toString(),
            bikeLicense: bikeDetail?.bikeLicense ?? '',
            bikeManualUrl: bikeDetail?.bikeManualUrl ?? '',
            bikeIdentification: bikeDetail?.bikeIdentification ?? '',
            bookingLeadTime: (bikeDetail?.bookingLeadTime ?? DEFAULT_BOOKING_LEAD_TIME).toString(),
            bikeModel: bikeDetail?.bikeModel ?? '',
            bikeCapacityId: (bikeDetail?.bikeCapacityId ?? '').toString(),
            bikeManufacture: bikeDetail?.bikeManufacture ?? '',
            waitingTimeUntilNextBooking: (bikeDetail?.waitingTimeUntilNextBooking ?? '').toString(),
            bikeType: (bikeDetail?.bikeType ?? '').toString(),
            bikeTransmission: (bikeDetail?.bikeTransmission?.toUpperCase() ?? GEARBOX.MT).toString(),
            isShowWeb: bikeDetail?.isShowWeb ?? true,
            vehicleImageBeforeId: (bikeDetail?.vehicleImageBefore?.id ?? '').toString(),
            vehicleImageAfterId: (bikeDetail?.vehicleImageAfter?.id ?? '').toString(),
            vehicleImageLeftId: (bikeDetail?.vehicleImageLeft?.id ?? '').toString(),
            vehicleImageRightId: (bikeDetail?.vehicleImageRight?.id ?? '').toString(),
            certificateExpiryDate: bikeDetail?.certificateExpiryDate ? new Date(bikeDetail?.certificateExpiryDate) : undefined,
            certificateImageIds: (bikeDetail?.certificateImages?.map(item => item.id) ?? []),
            isSmartphoneHolder: bikeDetail?.isSmartphoneHolder ?? false,
            isEtc: bikeDetail?.isEtc ?? false,
            isUsbCharging: bikeDetail?.isUsbCharging ?? false,
            // isFallOverSafetySystem: bikeDetail?.isFallOverSafetySystem ?? false,
            isTopBox: bikeDetail?.isTopBox ?? false,
            note: bikeDetail?.note ?? '',
            // color: bikeDetail?.color ?? '',
            batteryReplacementTime: bikeDetail?.batteryReplacementTime ? new Date(bikeDetail?.batteryReplacementTime) : undefined,
            // rentalStartDate: bikeDetail?.rentalStartDate ? new Date(bikeDetail?.rentalStartDate) : undefined,
            pinCode: bikeDetail?.pinCode ?? '',
            compensation: String(bikeDetail?.compensation || ''),
            isRecommendation: bikeDetail?.isRecommendation ?? false,
            compulsoryInsuranceExpDate: bikeDetail?.compulsoryInsuranceExpDate ? new Date(bikeDetail?.compulsoryInsuranceExpDate) : undefined,
            commisisonRate: bikeDetail?.commisisonRate?.toString() || undefined
        });

        setUrlImagePreviewBefore(bikeDetail?.vehicleImageBefore?.filePath ?? '');
        setUrlImagePreviewAfter(bikeDetail?.vehicleImageAfter?.filePath ?? '');
        setUrlImagePreviewLeft(bikeDetail?.vehicleImageLeft?.filePath ?? '');
        setUrlImagePreviewRight(bikeDetail?.vehicleImageRight?.filePath ?? '');
        setFileCerUploads(bikeDetail?.certificateImages ?? []);

        setOnlyView(isOwner && bikeDetail?.approvestatus === APPROVE_BIKE_STATUS.Approve);
    }, [bikeDetail]);

    useEffect(() => {
        if (bikeDetail && bikeDetail?.id && accounts && accounts.length > 0) {
            if (bikeDetail.bikeOwnerId) {
                let findOwnerIndex = accounts.findIndex(acc => acc.id.toString() === bikeDetail.bikeOwnerId.toString());
                if (findOwnerIndex < 0) {
                    form.setValue('bikeOwnerId', '');
                }
            }
        }
    }, [bikeDetail, accounts]);

    useEffect(() => {
        if (isEk && isCreate) {
            form.setValue('bikeType', bikeTypesEK?.[0]?.id.toString());
        }
    }, [bikeTypesEK]);

    const fetchData = async () => {
        getCapacitieList({ type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS });
        getManufacturers();
        isEk ? getBikeTypesEK() : getBikeTypes();
        getAccounts({ isOwner: true, status: STATUS.Active }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });
        if (!isCreate && id) {
            getBikeDetail(+id);
        }
    };

    // function
    const handleUploadFileCertification = (fileList: FileResponse[]) => {
        let currentFileList = fileCerUploads ? [...fileCerUploads] : [];
        let currentFileTypeIsImage = fileList.some(file => file.mimeType && file.mimeType.includes('image'));

        if (currentFileTypeIsImage) {
            let numberImgFiles = currentFileList?.filter(file => file.mimeType && file.mimeType.includes('image')).length;

            if (numberImgFiles >= 3) {
                showAlertModal({
                    id: 'limit-error',
                    type: 'error',
                    content: t('messages.maximum_image_upload')
                });

                return;
            }
        }

        fileList.forEach((file) => {
            currentFileList.push({
                id: file.id,
                fileName: file.fileName,
                fileType: file.fileType,
                filePath: file.path,
                mimeType: file.mimeType
            });
        });

        setFileCerUploads([...currentFileList]);
        form.setValue('certificateImageIds', currentFileList.map(file => file.id.toString()));
    };

    const handleRemoveImage = (path: string) => {
        if (fileCerUploads) {
            let filterFileList = fileCerUploads?.filter(file => file.filePath !== path);
            setFileCerUploads(filterFileList);
            form.setValue('certificateImageIds', filterFileList.map(file => file.id));
        }
    };

    const onSubmit = async (data: FormData) => {
        if (!watchVehicleImageRight) {
            return;
        }

        // convert data
        const convertedData = {
            ...data,
            bikeCapacityId: Number(data?.bikeCapacityId),
            bikeOwnerId: Number(data?.bikeOwnerId),
            bikeType: Number(data?.bikeType),
            bookingLeadTime: Number(data?.bookingLeadTime),
            certificateImageIds: data?.certificateImageIds || [],
            vehicleImageBeforeId: Number(data?.vehicleImageBeforeId),
            vehicleImageAfterId: Number(data?.vehicleImageAfterId),
            vehicleImageLeftId: Number(data?.vehicleImageLeftId),
            vehicleImageRightId: Number(data?.vehicleImageRightId),
            note: data?.note === '' ? null : data?.note,
            waitingTimeUntilNextBooking: data?.waitingTimeUntilNextBooking === '' ? null : Number(data?.waitingTimeUntilNextBooking),
            pinCode: data?.pinCode === '' ? null : data?.pinCode,
            compensation: data?.compensation === '' ? null : Number(data?.compensation),
            commisisonRate: data?.commisisonRate === '' ? null : Number(data?.commisisonRate),
            certificateExpiryDate: data?.certificateExpiryDate === undefined ? null : formatDateTime(data?.certificateExpiryDate, DATE_YMD_2),
            batteryReplacementTime: data?.batteryReplacementTime === undefined ? null : formatDateTime(data?.batteryReplacementTime, DATE_YMD_2),
            // rentalStartDate: data?.rentalStartDate === undefined ? null : formatDateTime(data?.rentalStartDate, DATE_YMD_2),
            compulsoryInsuranceExpDate: data?.compulsoryInsuranceExpDate === undefined ? null : formatDateTime(data?.compulsoryInsuranceExpDate, DATE_YMD_2)
        };

        // delete unnecessary data
        let dataSubmit;

        if (isEk) {
            const { isSmartphoneHolder, isEtc, isUsbCharging, isTopBox, certificateExpiryDate, bikeCapacityId, bikeIdentification, ...rest } = convertedData;
            dataSubmit = rest;
        } else {
            const { batteryReplacementTime, ...rest } = convertedData;
            dataSubmit = rest;
        }

        // submit
        if (newId) {
            if (dataSubmit.isShowWeb === false && bikeDetail?.isShowWeb === true) {
                // check order of bike
                const result = await flowResult(chekBikeHasOrderBeforeOff({ id: newId }));
                if (result && result.listOrders.length > 0) {
                    showAlertModal({
                        id: 'modal-show-off',
                        title: '',
                        size: 'xl',
                        titleClassName: 'text-center max-w-none',
                        content: (
                            <div>
                                <p className='text-center text-primaryWeb'>{t('sentences.warning_bike_has_booking')}</p>
                                <p>
                                    <span className='font-semibold'>
                                        (
                                        {result.listOrders.join(' , ')}
                                        )
                                    </span>
                                </p>
                                <div className='mt-4 flex gap-4 justify-center'>
                                    <ButtonCus
                                        onClick={() => {
                                            hideModal('modal-show-off');
                                        }}
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                    >
                                        {t('button.cancel')}
                                    </ButtonCus>
                                    <ButtonCus
                                        type='button'
                                        onClick={async () => {
                                            hideModal('modal-show-off');
                                            const res = await flowResult(isEk ? updateBikeEk({ ...dataSubmit, id: +newId }) : updateBikeGas({ ...dataSubmit, id: +newId }));
                                            if (res) {
                                                showAlertModal({
                                                    id: 'update-success',
                                                    type: 'success',
                                                    content: t('messages.update_success')
                                                });
                                                getBikeDetail(+newId);
                                            }
                                        }}
                                        variant='default'
                                        size='sm'
                                    >
                                        {t('button.ok')}
                                    </ButtonCus>
                                </div>
                            </div>
                        ),
                        footerClassName: 'hidden'
                    });
                } else {
                    const res = await flowResult(isEk ? updateBikeEk({ ...dataSubmit, id: +newId }) : updateBikeGas({ ...dataSubmit, id: +newId }));
                    if (res) {
                        showAlertModal({
                            id: 'update-success',
                            type: 'success',
                            content: t('messages.update_success')
                        });
                        getBikeDetail(+newId);
                    }
                }
            } else {
                const res = await flowResult(isEk ? updateBikeEk({ ...dataSubmit, id: +newId }) : updateBikeGas({ ...dataSubmit, id: +newId }));
                if (res) {
                    showAlertModal({
                        id: 'update-success',
                        type: 'success',
                        content: t('messages.update_success')
                    });
                    getBikeDetail(+newId);
                }
            }
        } else {
            const res = await flowResult(isEk ? createBikeEk(dataSubmit) : createBikeGas(dataSubmit));
            if (res) {
                // toastify('success', t('messages.add_success'));
                showAlertModal({
                    id: 'add-success',
                    type: 'success',
                    content: t('messages.add_success'),
                    onClickXIcon: () => {
                        onComplete && onComplete();
                    }
                });
            }
        }
    };

    const onInvalid = (errors: any) => {
        let hasError = false;
        if (!watchVehicleImageRight) {
            hasError = true;
        }

        if (isEk && (!watchCertificateImageIds || watchCertificateImageIds.length === 0)) {
            hasError = true;
        }
        if (hasError) {
            return;
        }
    };

    const handleClickDeleteBike = async (code: number | string | undefined) => {
        if (code) {
            showAlertModal({
                id: 'delete-bike',
                isConfirmModal: true,
                content: t('messages.confirm_delete'),
                cancelButton: t('button.cancel'),
                saveButton: t('button.delete'),
                onSave: async () => {
                    hideModal('delete-bike');
                    const res = await flowResult(deleteBike(Number(code)));
                    if (res) {
                        showAlertModal({
                            id: 'delete-success',
                            type: 'success',
                            content: t('messages.delete_success'),
                            onClickXIcon: () => {
                                navigate(isEk ?
                                    `/${ekDomain}/${ROUTES.bikeManagement.hrefEK}` :
                                    `${ROUTES.bikeManagement.href}`);
                            }
                        });
                    }
                }
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
                <div className='grid grid-cols-1 point-600:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 layout-card rounded-t-none bg-white'>
                    <h3 className='font-semibold text-[1.125rem] col-span-full items-end'>{t('words_title.basic_info')}</h3>

                    {isOwner ?
                        (
                            <div>
                                <FormLabel required>{t('field.owner')}</FormLabel>
                                <Input className='' disabled value={isEk ? profileEk?.fullname : profileGas?.fullname} />
                            </div>
                        ) :
                        (
                            <FormField
                                control={form.control}
                                name='bikeOwnerId'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col justify-start'>
                                        <FormLabel required>{t('field.owner')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                disabled={isView || isOwner}
                                                disableClearIcon={isOwner || isOnlyView}
                                                placeholder={t('placeholder.choose')}
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                }}
                                                // search={false}
                                                items={
                                                    (Array.isArray(accounts) && accounts?.length ?
                                                        [
                                                            ...accounts
                                                                .filter(account => (
                                                                    Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_EK &&
                                                                    Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_RD
                                                                ))
                                                                .map(item => ({
                                                                    label: item.fullname,
                                                                    labelSearch: item.fullname + item?.userCode,
                                                                    value: item.id.toString(),
                                                                    code: item.id
                                                                }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                    <FormField
                        control={form.control}
                        name='bikeManufacture'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel required>{t('field.manufacture')}</FormLabel>
                                <FormControl>
                                    {/* eslint-disable-next-line @stylistic/multiline-ternary */}
                                    {isEk ? (
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                            disabled={isOnlyView}
                                        />
                                    ) :
                                        (
                                            <Combobox
                                                disabled={isOnlyView}
                                                disableClearIcon={isOnlyView}
                                                placeholder={t('placeholder.choose')}
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                }}
                                                // search={false}
                                                items={
                                                    (Array.isArray(manufacturers) && manufacturers?.length ?
                                                        [
                                                            ...manufacturers.map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.name_jp : item.name,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        )}
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name='bikeModel'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel required>{t('field.model_name')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        {...field}
                                        disabled={isOnlyView}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {isEk ?
                        (
                            <FormField
                                control={form.control}
                                name='bikeType'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col'>
                                        <FormLabel className=''>{t('field.bike_type')}</FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                }}
                                                value={field.value}
                                                className='flex gap-6'
                                                disabled={isOnlyView}
                                            >
                                                {bikeTypesEK?.map((item, index) => (
                                                    <div key={index} className='flex items-center space-x-2'>
                                                        <RadioGroupItem value={item.id.toString()} id={item.id.toString()} />
                                                        <Label className='cursor-pointer font-normal' htmlFor={item.id.toString()}>{language === LANGUAGES_SUPPORTED.Japanese ? item.name_jp : item.name}</Label>
                                                    </div>
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        ) :
                        (
                            <FormField
                                control={form.control}
                                name='bikeCapacityId'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col justify-start'>
                                        <FormLabel required>{t('field.engine_size')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                disabled={isOnlyView}
                                                disableClearIcon={isOnlyView}
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                }}
                                                search={false}
                                                items={
                                                    (Array.isArray(capacitieList) && capacitieList?.length ?
                                                        [
                                                            ...capacitieList.map(item => ({
                                                                label: t(`words_title.${item.code}`) ?? '',
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                    {
                        !isEk && (
                            <FormField
                                control={form.control}
                                name='bikeTransmission'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col'>
                                        <FormLabel className=''>{t('field.gearbox')}</FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                }}
                                                value={field.value}
                                                className='flex gap-6'
                                                disabled={isOnlyView}
                                            >
                                                <div className='flex items-center space-x-2'>
                                                    <RadioGroupItem value={GEARBOX.MT} id='mt' />
                                                    <Label className='cursor-pointer font-normal' htmlFor='mt'>{t('words_title.mt')}</Label>
                                                </div>
                                                <div className='flex items-center space-x-2'>
                                                    <RadioGroupItem value={GEARBOX.AT} id='at' />
                                                    <Label className='cursor-pointer font-normal' htmlFor='at'>{t('words_title.at')}</Label>
                                                </div>
                                            </RadioGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )
                    }
                    {!isEk && (
                        <FormField
                            control={form.control}
                            name='bikeType'
                            render={({ field }) => (
                                <FormItem className='flex flex-col justify-start'>
                                    <FormLabel required>{t('field.bike_type')}</FormLabel>
                                    <FormControl>
                                        <Combobox
                                            disabled={isOnlyView}
                                            disableClearIcon={isOnlyView}
                                            placeholder={t('placeholder.choose')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.code);
                                            }}
                                            // search={false}
                                            items={
                                                (Array.isArray(bikeTypes) && bikeTypes?.length ?
                                                    [
                                                        ...bikeTypes.map(item => ({
                                                            label: language === LANGUAGES_SUPPORTED.Japanese ? item.name_jp : item.name,
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    {!isEk && (
                        <FormField
                            control={form.control}
                            name='bikeIdentification'
                            render={({ field }) => (
                                <FormItem className='flex flex-col justify-start'>
                                    <FormLabel required>{t('field.chasssis_number')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            isHalfWidth
                                            {...field}
                                            disabled={isOnlyView}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    <FormField
                        control={form.control}
                        name='bikeLicense'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel required>{t('field.license_plate')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        {...field}
                                        disabled={isOnlyView}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='bikeManualUrl'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel>{t('field.user_manual')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        isHalfWidth
                                        maxLength={MAX_LENGTH_LINK}
                                        {...field}
                                        disabled={isOnlyView}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {
                        !isEk && (
                            <FormField
                                control={form.control}
                                name='pinCode'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col justify-start'>
                                        <FormLabel required>{t('field.pin_code')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                                disabled={false}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )
                    }
                    {
                        (isOnlyView || !isOwner) && (
                            <>
                                <FormField
                                    control={form.control}
                                    name='compensation'
                                    render={({ field }) => (
                                        <FormItem className='flex flex-col justify-start'>
                                            <FormLabel>{t('field.insurance_pre', { currency: '' })}</FormLabel>
                                            <FormControl>
                                                <FormatNumberVer2
                                                    decimalScale={0}
                                                    prefix={t('currency.currency_symbol')}
                                                    placeholder={t('placeholder.enter')}
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    setvalueOfNumericFormat={field.onChange}
                                                    // onPaste={e => e.preventDefault()}
                                                    disabled={isOnlyView}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='commisisonRate'
                                    render={({ field }) => (
                                        <FormItem className='flex flex-col justify-start'>
                                            <FormLabel>{t('field.return_rate')}</FormLabel>
                                            <FormControl>
                                                <FormatNumberVer2
                                                    decimalScale={0}
                                                    suffix='%'
                                                    placeholder={t('placeholder.enter')}
                                                    statusDisplay='input'
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    value={field.value}
                                                    setvalueOfNumericFormat={field.onChange}
                                                    // onPaste={e => e.preventDefault()}
                                                    disabled={isOnlyView}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </>
                        )
                    }
                    <FormField
                        control={form.control}
                        name='bookingLeadTime'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel required>{t('field.rental_reservation_deadline')}</FormLabel>
                                <FormControl>
                                    <FormatNumberVer2
                                        decimalScale={0}
                                        suffix='時間前'
                                        placeholder={t('placeholder.enter')}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        value={field.value}
                                        setvalueOfNumericFormat={field.onChange}
                                        // onPaste={e => e.preventDefault()}
                                        disabled={false}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name='waitingTimeUntilNextBooking'
                        render={({ field }) => (
                            <FormItem className='flex flex-col justify-start'>
                                <FormLabel>{t('field.waiting_time_until_next_booking_hours')}</FormLabel>
                                <FormControl>
                                    <FormatNumberVer2
                                        decimalScale={0}
                                        suffix='時間後'
                                        placeholder={t('placeholder.enter')}
                                        statusDisplay='input'
                                        thousandSeparator={true}
                                        allowNegative={false}
                                        value={field.value}
                                        setvalueOfNumericFormat={field.onChange}
                                        // onPaste={e => e.preventDefault()}
                                        disabled={false}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className='col-span-full'>
                        <FormField
                            control={form.control}
                            name='isShowWeb'
                            render={({ field }) => (
                                <FormItem className='flex gap-4 items-center'>
                                    <FormLabel className='mb-0'>{t('field.display_on_website')}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            key={field.value?.toString()}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                            }}
                                            value={field.value.toString()}
                                            className='flex gap-6 items-center'
                                            disabled={isOnlyView}
                                        >
                                            {[
                                                { value: true, label: t('field.yes') },
                                                { value: false, label: t('field.no') }
                                            ].map(option => (
                                                <div
                                                    key={`isShowWeb-${option.value}`}
                                                    className='flex items-center space-x-2'
                                                >
                                                    <RadioGroupItem
                                                        value={option.value.toString()}
                                                        id={`isShowWeb-${option.value}`}
                                                    />
                                                    <Label
                                                        className='cursor-pointer font-normal'
                                                        htmlFor={`isShowWeb-${option.value}`}
                                                    >
                                                        {option.label}
                                                    </Label>
                                                </div>
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormLabel required className='col-span-full -mb-2 md:-mb-6'>{t('field.bike_image')}</FormLabel>
                    <ImageUpload
                        filedName='vehicleImageRightId'
                        form={form}
                        caption={(
                            <>
                                {t('words_title.right')}
                                <span className='text-customRed font-bold'> *</span>
                            </>
                        )}
                        required={true}
                        disabled={isOnlyView}
                        urlImagePreview={urlImagePreviewRight}
                    />
                    <ImageUpload
                        filedName='vehicleImageLeftId'
                        form={form}
                        caption={t('words_title.left')}
                        required={false}
                        disabled={false}
                        urlImagePreview={urlImagePreviewLeft}
                    />
                    <ImageUpload
                        filedName='vehicleImageBeforeId'
                        form={form}
                        caption={t('words_title.other_side', { number: 1 })}
                        required={false}
                        disabled={false}
                        urlImagePreview={urlImagePreviewBefore}
                    />
                    <ImageUpload
                        filedName='vehicleImageAfterId'
                        form={form}
                        caption={t('words_title.other_side', { number: 2 })}
                        required={false}
                        disabled={false}
                        urlImagePreview={urlImagePreviewAfter}
                    />
                    {/* {imgError && <span className='col-span-full text-red-500 -mt-4'>{imgError}</span>} */}
                    <div className='col-span-full flex flex-col gap-2.5'>
                        <h3 className='font-semibold text-[1.125rem] col-span-full'>{isEk ? t('words_title.mainttenance_infor') : t('words_title.vehicle_registration_certificate')}</h3>
                        <p className='whitespace-pre-wrap text-[#838383] text-fs-mid14 leading-[calc(24/14)]'>{isEk ? t('sentences.compulsory_insurance_expiration_note') : t('words_title.vehicle_registration_deadline_note')}</p>
                        <div className='w-full grid grid-cols-1 point-600:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6'>
                            {
                                !(isEk && fileCerUploads && fileCerUploads?.length > 0) && (
                                    <FileUpload
                                        fieldName='certificateImageIds'
                                        form={form}
                                        required={isEk}
                                        className={cn('w-full')}
                                        placeholder={isEk ? t('field.choose_image') : t('sentences.choose_img_pdf')}
                                        acceptUpload={isEk ? 'image/*' : 'image/*, .pdf'}
                                        handleUploadFile={handleUploadFileCertification}
                                    />
                                )
                            }

                            {
                                fileCerUploads && fileCerUploads?.length > 0 && (
                                    <>
                                        {fileCerUploads.filter(file => file.mimeType && file.mimeType.includes('image')).map((file, index) => {
                                            return (
                                                <div key={index}>
                                                    <ImageUploadShow isShowX clearImg={handleRemoveImage} urlImagePreview={file.filePath || ''} />
                                                </div>
                                            );
                                        })}
                                    </>
                                )
                            }
                        </div>

                        <div className='w-full space-y-2'>
                            {
                                fileCerUploads && fileCerUploads?.length > 0 && (
                                    <>
                                        {fileCerUploads.filter(file => file.mimeType && !file.mimeType.includes('image')).map((file, index) => {
                                            return (
                                                <div key={index} className='flex xl:w-1/3 point-600:w-2/3 w-full gap-2 justify-between items-center bg-[#E6F7FD] px-2 py-1 rounded-[6px]'>
                                                    <a href={file.filePath || ''} target='_blank' className='line-clamp-2 text-[#3B7CEC] hover:underline cursor-pointer break-all' rel='noreferrer'>
                                                        {file.fileName}
                                                    </a>
                                                    <div className={classNames('size-5 cursor-pointer')} onClick={() => handleRemoveImage(file.filePath || '')}>
                                                        <Trash2 stroke='#FE1212' />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )
                            }
                        </div>
                        {/* {imgCertificateError && <span className='col-span-full text-red-500 -mt-4'>{imgCertificateError}</span>} */}

                        {
                            !isEk && (
                                <div className='w-full grid grid-cols-1 point-600:grid-cols-2 gap-4 md:gap-6'>
                                    <FormField
                                        control={form.control}
                                        name='certificateExpiryDate'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t('field.vehicle_registration_deadline')}</FormLabel>
                                                <FormControl>
                                                    <Calendar
                                                        calendarDisabled={false}
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={date => field.onChange(date)}
                                                        useIconRemove={true}
                                                        required={false}
                                                        disabled={false}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )
                        }

                        <div className='w-full grid grid-cols-1 point-600:grid-cols-2 gap-4 md:gap-6'>
                            <FormField
                                control={form.control}
                                name='compulsoryInsuranceExpDate'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.compulsory_insurance_expiration')}</FormLabel>
                                        <FormControl>
                                            <Calendar
                                                calendarDisabled={false}
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={true}
                                                required={false}
                                                disabled={false}
                                                isInvalid={!!fieldState.error}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        {isEk &&
                            (
                                <>
                                    <div className='w-full grid grid-cols-1 point-600:grid-cols-2 gap-4 md:gap-6'>
                                        <FormField
                                            control={form.control}
                                            name='batteryReplacementTime'
                                            render={({ field, fieldState }) => (
                                                <FormItem className='flex flex-col justify-start'>
                                                    <FormLabel required>{t('field.battery_replacement_time')}</FormLabel>
                                                    <FormControl>
                                                        <Calendar
                                                            mode='single'
                                                            selected={field.value}
                                                            onSelect={date => field.onChange(date)}
                                                            useIconRemove={true}
                                                            required={false}
                                                            disabled={isOnlyView}
                                                            isInvalid={!!fieldState.error}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                    </div>
                                    <p className='whitespace-pre-wrap text-[#838383]'>{t('sentences.battery_replacement_time_note')}</p>
                                </>

                            )}
                        {isEk && (
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col justify-start col-span-full'>
                                        <FormLabel>{t('field.note')}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-28'
                                                maxLength={500}
                                                {...field}
                                                disabled={isOnlyView}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                    </div>
                </div>
                {!isEk && (
                    <div className='grid grid-cols-1 xsm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 layout-card bg-white mt-4 md:mt-6'>
                        <h3 className='font-semibold text-[1.125rem] col-span-full items-end'>{t('words_title.accessories')}</h3>
                        {accessoryFieldsRadio.map(({ name, label }) => (
                            <FormField
                                key={name}
                                control={form.control}
                                name={name}
                                render={({ field }) => (
                                    <FormItem className='flex flex-col md:gap-2.5'>
                                        <FormLabel className='mb-2 md:mb-0 min-w-32 md:min-w-40'>
                                            {label}
                                        </FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                key={field.value?.toString()}
                                                onValueChange={value => field.onChange(value)}
                                                value={field.value?.toString()}
                                                className='flex gap-12'
                                                disabled={false}
                                            >
                                                {[
                                                    { value: true, label: t('field.yes') },
                                                    { value: false, label: t('field.no') }
                                                ].map(option => (
                                                    <div
                                                        key={`${name}-${option.value}`}
                                                        className='flex items-center space-x-2'
                                                    >
                                                        <RadioGroupItem
                                                            value={option.value.toString()}
                                                            id={`${name}-${option.value}`}
                                                        />
                                                        <Label
                                                            className='cursor-pointer font-normal'
                                                            htmlFor={`${name}-${option.value}`}
                                                        >
                                                            {option.label}
                                                        </Label>
                                                    </div>
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        ))}
                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem className='flex flex-col justify-start col-span-full'>
                                    <FormLabel>{t('field.note')}</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            disabled={isOnlyView}
                                            placeholder={t('placeholder.enter')}
                                            className='min-h-28'
                                            maxLength={500}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {isEdit && (
                            <div className='col-span-full text-[#1A1D1F] space-y-2'>
                                <div className='flex gap-4'>
                                    <label className='w-28 font-medium flex-none'>{t('field.create_by')}</label>
                                    <span className='flex-auto break-all'>{bikeDetail?.creater?.mail}</span>
                                </div>
                                <div className='flex gap-4'>
                                    <label className='w-28 font-medium flex-none'>{t('field.update_time')}</label>
                                    <span className='flex-auto break-all'>{formatDateTime(bikeDetail?.updatedAt, DISPPLAY_MOMENT_YMD_HM) || '--'}</span>
                                </div>

                                {bikeDetail?.reasion && bikeDetail?.reasion?.length > 0 && (
                                    <div className='space-y-2'>
                                        <label className='font-medium'>{t('field.approve_rejeject_resion')}</label>
                                        {bikeDetail?.reasion?.map((item, index) => (
                                            <div key={index} className='flex gap-4 ml-4'>
                                                <p className='min-w-[120px]'>{formatDateTime(item.createdAt, DISPPLAY_MOMENT_YMD_HM) + ':'}</p>
                                                <p className='break-all'>{item.approveNote || '--'}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {
                    (!isOwner || (isOwner && bikeDetail?.approvestatus === APPROVE_BIKE_STATUS.Approve) || isOnlyView) && (
                        <div className='layout-card bg-white mt-4 md:mt-6'>
                            <h3 className='text-[1.125rem] font-semibold'>{t('words_title.admin_set_recommendation')}</h3>
                            <FormField
                                control={form.control}
                                name='isRecommendation'
                                render={({ field }) => (
                                    <FormItem className={classNames('flex gap-2 items-center mt-4', !isView && ' cursor-pointer')}>
                                        <FormControl>
                                            <Checkbox
                                                disabled={isOnlyView}
                                                checked={field.value}
                                                onCheckedChange={checked => field.onChange(checked)}
                                            />
                                        </FormControl>
                                        <Label className='font-normal'>{t('words_title.recommendation')}</Label>
                                    </FormItem>
                                )}
                            />
                        </div>
                    )
                }
                <div className='point-600:mb-0 mb-20'></div>
                <div className='mt-4 md:mt-6 flex justify-center gap-4 point-600:static point-600:w-full w-screen fixed left-0 bottom-0 point-600:py-0 py-3 point-600:bg-inherit bg-background-3 point-600:shadow-none shadow-shadow-3'>
                    {
                        isEdit && newId && (
                            <ButtonCusWithIcon
                                type='button'
                                variant='outline'
                                size='sm'
                                iconId='#icon-delete'
                                onClick={() => {
                                    handleClickDeleteBike(newId);
                                }}
                                disabled={isOwner}
                            >
                                {t('button.delete')}
                            </ButtonCusWithIcon>
                        )
                    }
                    {isCreate ?
                        (
                            <ButtonCus
                                variant='default'
                                size='sm'
                                disabled={form.formState.isSubmitting}
                            >
                                {t('button.add')}
                            </ButtonCus>
                        ) :
                        (
                            <ButtonCusWithIcon
                                type='submit'
                                variant='default'
                                size='sm'
                                iconId='#icon-update'
                                disabled={form.formState.isSubmitting}
                            >
                                {t('button.update')}
                            </ButtonCusWithIcon>
                        )}
                </div>
            </form>
        </Form>
    );
});
